import React, { useState } from 'react';
import { useRateLimit, formatTimeRemaining } from '../hooks/useRateLimit';
import { sanitizeInput, validateDreamContent } from '../utils/sanitize';
interface DreamInputProps {
  onSubmit: (text: string) => void;
}
// Rate limit: 5 submissions per hour
const RATE_LIMIT_CONFIG = {
  maxAttempts: 5,
  windowMs: 60 * 60 * 1000,
  storageKey: 'dream_submissions'
};
const MAX_CHARS = 2000;
export function DreamInput({
  onSubmit
}: DreamInputProps) {
  const [text, setText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState(''); // Bot trap
  const [rateLimitState, recordAttempt] = useRateLimit(RATE_LIMIT_CONFIG);
  const handleSubmit = () => {
    // Honeypot check - if filled, silently reject (bots fill hidden fields)
    if (honeypot) {
      setText('');
      return;
    }
    // Rate limit check
    if (!rateLimitState.canSubmit) {
      setError(`You've shared quite a bit recently. Please wait ${formatTimeRemaining(rateLimitState.timeUntilReset || 0)} before sharing again.`);
      return;
    }
    // Validate content
    const validation = validateDreamContent(text);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }
    // Sanitize and submit
    const sanitized = sanitizeInput(text);
    // Record the attempt
    if (!recordAttempt()) {
      setError('Please wait a moment before sharing again.');
      return;
    }
    onSubmit(sanitized);
    setText('');
    setError(null);
  };
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    if (newText.length <= MAX_CHARS) {
      setText(newText);
      setError(null);
    }
  };
  const charsRemaining = MAX_CHARS - text.length;
  const isNearLimit = charsRemaining < 200;
  return <div className="w-full max-w-2xl mx-auto mb-12 relative group" data-id="element-33">
      {/* Paper Stack Effect */}
      <div className="absolute inset-0 bg-[#f0ebd8] transform rotate-1 rounded-sm shadow-sm translate-y-2 translate-x-2" data-id="element-34" />
      <div className="absolute inset-0 bg-[#f5f0e0] transform -rotate-1 rounded-sm shadow-sm translate-y-1 translate-x-1" data-id="element-35" />

      {/* Main Input Paper */}
      <div className={`relative bg-[#fffef9] p-6 pt-8 rounded-sm shadow-md transition-shadow duration-300 ${isFocused ? 'shadow-lg' : ''}`} data-id="element-36">
        {/* Red Margin Line */}
        <div className="absolute top-0 bottom-0 left-12 w-px bg-red-300/50 z-10" data-id="element-37" />

        <div className="relative z-20" data-id="element-38">
          <label htmlFor="dream-input" className="block text-xl font-serif text-[#8b7355] mb-4 pl-8 italic" data-id="element-39">
            Right now, I'm carrying...
          </label>

          {/* Honeypot field - hidden from humans, visible to bots */}
          <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{
          position: 'absolute',
          left: '-9999px',
          opacity: 0,
          height: 0,
          width: 0,
          pointerEvents: 'none'
        }} data-id="element-40" />

          <textarea id="dream-input" value={text} onChange={handleTextChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder="A thought, hope, fear, or a feeling..." className="w-full min-h-[160px] bg-transparent border-none resize-none focus:ring-0 p-0 pl-8 text-xl md:text-2xl text-[#5c4033] font-handwriting leading-[2rem] placeholder:text-[#d4c4a8] placeholder:font-serif placeholder:italic" style={{
          backgroundImage: 'linear-gradient(transparent 95%, #e5e0d0 95%)',
          backgroundSize: '100% 2rem',
          backgroundAttachment: 'local'
        }} data-id="element-41" />

          {/* Error message */}
          {error && <p className="pl-8 text-red-700/80 font-serif italic text-sm mt-2" data-id="element-42">
              {error}
            </p>}

          <div className="flex items-center justify-between mt-4 border-t border-[#e5e0d0] pt-4" data-id="element-43">
            {/* Character count & rate limit info */}
            <div className="flex items-center gap-4 pl-8" data-id="element-44">
              <span className={`font-serif text-sm ${isNearLimit ? 'text-red-700/70' : 'text-[#8b7355]/60'}`} data-id="element-45">
                {charsRemaining} characters left
              </span>
              {rateLimitState.remainingAttempts < 5 && <span className="font-serif text-sm text-[#8b7355]/60" data-id="element-46">
                  · {rateLimitState.remainingAttempts} shares remaining
                </span>}
            </div>

            <button onClick={handleSubmit} disabled={!text.trim() || !rateLimitState.canSubmit} className="px-6 py-2 font-serif text-lg text-[#5c4033] hover:text-[#3a2820] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-47">
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>;
}